import { ConnectionItem, Flex, Image, Link, StatusBanner, Text } from "@wayflyer/flyui";
import { useState, type ReactNode } from "react";
import { useIntl } from "react-intl";
import { connectByPath } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";

import { AuthCallbackPath } from "../../../api-types/connector-callbacks";
import { usePlatformConnectorProperties } from "../../../hooks/connector";
import { ConnectionsList } from "../common/connections";
import { OAuthErrorBanner } from "../common/oauth-error-banner";

import { SHOPIFY_MESSAGES } from "./message-definitions";
import { ShopifyInitialConnection } from "./shopify-initial-connection";

const CONNECTOR_SLUG = "shopify-appstore";

interface ShopifyAppStoreConnectProps {
  postConnectPath: AuthCallbackPath;
  fullWidthImage?: boolean;
  onInitiateConnection?: () => void;
}

export const ShopifyAppStoreConnect = ({
  postConnectPath,
  fullWidthImage,
  onInitiateConnection,
}: ShopifyAppStoreConnectProps) => {
  const intl = useIntl();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { data: connectorProperties } = usePlatformConnectorProperties(customerDomain, CONNECTOR_SLUG, postConnectPath);
  const [connectionInProgress, setConnectionInProgress] = useState(false);

  const connectHandler = async () => {
    setConnectionInProgress(true);
    onInitiateConnection?.();
    await connectByPath(connectorProperties?.connectPath);
  };

  return (
    <Flex direction="column" gap="8" align="flex-start" justify="space-between">
      <OAuthErrorBanner connectorSlug={CONNECTOR_SLUG} />
      <Text>{intl.formatMessage(SHOPIFY_MESSAGES.shopifyDataUsage)}</Text>
      {connectorProperties && !!connectorProperties.connections.length ? (
        <ConnectionsList
          connectorProperties={connectorProperties}
          connectForm={
            <Flex direction="column">
              <StatusBanner
                variant="info"
                subheading={intl.formatMessage(SHOPIFY_MESSAGES.shopifyAdditionalStoreInstructions, {
                  link: (chunks: ReactNode) => (
                    <Link
                      url="https://help-center.wayflyer.com/en/articles/210531-how-do-i-connect-my-shopify-store"
                      external
                    >
                      {chunks}
                    </Link>
                  ),
                  strong: (chunks: ReactNode) => (
                    <Text element="span" fontWeight="semiBold">
                      {chunks}
                    </Text>
                  ),
                })}
              />
              <ConnectionItem
                key="shopify"
                label="Shopify"
                logo={
                  connectorProperties.connectionLogoSrc ? <Image src={connectorProperties.connectionLogoSrc} /> : null
                }
                actions={[
                  {
                    onAction() {
                      connectHandler();
                    },
                    variant: "Primary",
                    content: intl.formatMessage(SHOPIFY_MESSAGES.shopifyConnectStoreLabel),
                    loading: connectionInProgress,
                  },
                ]}
              />
            </Flex>
          }
        />
      ) : (
        <ShopifyInitialConnection connectHandler={connectHandler} fullWidthImage={fullWidthImage} />
      )}
    </Flex>
  );
};
